import { SectionHeading } from '@components/section-heading';
import React, { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Checkbox } from '@components/checkbox';
import { Combobox } from '@components/combobox';
import { CurrencySelect } from '@components/currency-select';
import { Select } from '@components/select';
import { DatePicker } from '@components/date-picker';
import { FieldLabel, TextField } from '@components/textfield';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger
} from '@components/ui/accordion';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Label } from '@components/ui/label';
import { Separator } from '@components/ui/separator';
import { Switch } from '@components/ui/switch';
import {
    CommunityDailyGoalRequestV3Dto,
    CommunityGoalAdminUpdateRequestDto,
    GoalAggregationType,
    GoalDto,
    GoalFitnessMetricType,
    GoalRuleType,
    RecurrenceType,
    RewardingType,
    GoalRuleDto,
    GoalRuleLevelDto,
    RewardConfigDto,
    WorkoutCategory
} from '@api/FitnessActivityClient';
import {
    buildGoalActivityFilterPayload,
    goalActivityFiltersFromSource,
    GoalActivityFiltersEditor,
    GoalActivityFilterFormValue
} from '@core/goals/activity-filters';
import {
    describeGoal,
    describeReward,
    goalActivityScope,
    goalTitle,
    recurrenceLabels
} from '@core/goals/goal-summary';
import { BadgePicker, BadgePickerOption } from '@components/badge-picker';

interface GoalModalProps {
    open: boolean;
    onClose: () => void;
    onCreate: (
        goal: CommunityDailyGoalRequestV3Dto,
        assignAllUsers: boolean
    ) => Promise<boolean>;
    onUpdate: (
        key: string,
        goal: CommunityGoalAdminUpdateRequestDto
    ) => Promise<boolean>;
    communityId: number;
    goal?: GoalDto;
}

const rewardConditions: { value: RewardingType; label: string }[] = [
    { value: RewardingType.GOAL_REACHED, label: 'Goal Reached' },
    { value: RewardingType.LEVEL_INCREASED, label: 'Level Increased' },
    { value: RewardingType.STREAK_INCREASED, label: 'Streak Increased' }
];

export const workoutCategories: {
    value: WorkoutCategory | undefined;
    label: string;
}[] = [
    { value: undefined, label: 'None' },
    { value: WorkoutCategory.FOOT, label: 'Foot' },
    { value: WorkoutCategory.WHEELS, label: 'Wheels' },
    { value: WorkoutCategory.WATER, label: 'Water' },
    { value: WorkoutCategory.WINTER, label: 'Winter' },
    { value: WorkoutCategory.TEAM_SPORTS, label: 'Team Sports' },
    {
        value: WorkoutCategory.STATIONARY_TRAININGS,
        label: 'Stationary Trainings'
    },
    { value: WorkoutCategory.OTHER, label: 'Other' }
];

const fitnessMetricLabels: Record<GoalFitnessMetricType, string> = {
    [GoalFitnessMetricType.STEPS]: 'Steps',
    [GoalFitnessMetricType.DISTANCE]: 'Distance',
    [GoalFitnessMetricType.ACTIVITY_TIME_IN_MINUTES]: 'Minutes',
    [GoalFitnessMetricType.CALORIES]: 'Calories'
};

const ruleTypeOptions = [
    { value: GoalRuleType.ACTIVITY_POINTS, label: 'Activity Points' },
    { value: GoalRuleType.ACTIVITY_RAW_VALUE, label: 'Activity Raw Value' },
    { value: GoalRuleType.ACTIVITY_COUNT, label: 'Activity Count' }
];

const fitnessMetricOptions = Object.values(GoalFitnessMetricType).map(
    (metric) => ({ value: metric, label: fitnessMetricLabels[metric] })
);

const aggregationTypeOptions = [
    { value: GoalAggregationType.SUM, label: 'Sum' },
    { value: GoalAggregationType.MAX, label: 'Max' }
];

const rewardKindOptions: { value: RewardKind; label: string }[] = [
    { value: 'CURRENCY', label: 'Currency' },
    { value: 'BADGE', label: 'Badge' }
];

const recurrenceOptions = Object.values(RecurrenceType).map((value) => ({
    value,
    label: recurrenceLabels[value]
}));

const timeZoneOptions = (): string[] => {
    const supported = Intl as typeof Intl & {
        supportedValuesOf?: (key: string) => string[];
    };
    return supported.supportedValuesOf?.('timeZone') ?? ['Europe/Warsaw'];
};

type RewardKind = 'CURRENCY' | 'BADGE';

interface RewardRow {
    condition: RewardingType;
    kind: RewardKind;
    value: number;
    currencyCode: string;
    badge: BadgePickerOption | null;
    minLevelValue: number;
    minStreakValue: number;
    maxRewards: number;
    maxRewardsPeriod: RecurrenceType | '';
}

const rewardLimitPeriods: { value: RecurrenceType; label: string }[] = [
    { value: RecurrenceType.DAILY, label: 'Per day' },
    { value: RecurrenceType.WEEKLY, label: 'Per week' },
    { value: RecurrenceType.MONTHLY, label: 'Per month' },
    { value: RecurrenceType.LIFETIME, label: 'Ever' }
];

const rewardLimitOptions: { value: RecurrenceType | ''; label: string }[] = [
    { value: '', label: 'No limit' },
    ...rewardLimitPeriods
];

const rewardRowsFromConfigs = (
    rewardConfigs: RewardConfigDto[],
    defaultCurrencyCode: string
): RewardRow[] =>
    rewardConfigs.map((config) => ({
        condition: config.rewardingType,
        kind: config.badgeLevelId != null ? 'BADGE' : 'CURRENCY',
        value: config.value ?? 1,
        currencyCode: config.currencyCode || defaultCurrencyCode,
        badge: config.badgeLevel
            ? {
                  id: config.badgeLevel.badgeTypeId,
                  name: config.badgeLevel.name,
                  categoryName: {},
                  levelIndex: config.badgeLevel.levelIndex,
                  badgeLevelId: config.badgeLevel.id
              }
            : null,
        minLevelValue: config.minLevelValue ?? 1,
        minStreakValue: config.minStreakValue ?? 2,
        maxRewards: config.limit?.maxRewards ?? 1,
        maxRewardsPeriod: config.limit?.maxRewardsPeriod ?? ''
    }));

const hasSavedRewardLimit = (goal?: GoalDto): boolean =>
    !!goal?.rewardConfigs.some((config) => config.limit);

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

const SwitchField = ({
    label,
    checked,
    onChange
}: {
    label: string;
    checked: boolean;
    onChange(checked: boolean): void;
}) => {
    const id = React.useId();
    return (
        <div className="flex items-center gap-2">
            <Switch id={id} checked={checked} onCheckedChange={onChange} />
            <Label htmlFor={id}>{label}</Label>
        </div>
    );
};

const toIsoDay = (value: Date | null) => {
    if (!value) {
        return '';
    }
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
};

const fromIsoDay = (value: string) =>
    value ? new Date(`${value}T00:00:00`) : null;

const DateField = ({
    label,
    value,
    hint,
    onChange
}: {
    label: string;
    value: string;
    hint: string;
    onChange(value: string): void;
}) => {
    return (
        <div className="flex w-full flex-col gap-1.5">
            <DatePicker
                label={label}
                clearable
                value={fromIsoDay(value)}
                onChange={(next) => onChange(toIsoDay(next))}
            />
            <Hint>{hint}</Hint>
        </div>
    );
};

const UserIdsInput = ({
    value,
    onChange
}: {
    value: string[];
    onChange(value: string[]): void;
}) => {
    const [draft, setDraft] = useState('');

    const commit = () => {
        const ids = draft
            .split(/[\s,]+/)
            .map((id) => id.trim())
            .filter((id) => id && !value.includes(id));
        if (ids.length) {
            onChange([...value, ...ids]);
        }
        setDraft('');
    };

    return (
        <div className="flex flex-col gap-1.5">
            <FieldLabel label="User IDs to Assign" />
            <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border px-2 py-1">
                {value.map((id) => (
                    <Badge key={id} variant="secondary" className="gap-1">
                        {id}
                        <X
                            className="size-3 cursor-pointer"
                            onClick={() =>
                                onChange(value.filter((v) => v !== id))
                            }
                        />
                    </Badge>
                ))}
                <input
                    className="min-w-[120px] flex-1 bg-transparent text-sm outline-none"
                    placeholder="Add user IDs"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            commit();
                        }
                        if (e.key === 'Backspace' && !draft && value.length) {
                            onChange(value.slice(0, -1));
                        }
                    }}
                />
            </div>
        </div>
    );
};

export const GoalModal: React.FC<GoalModalProps> = ({
    open,
    onClose,
    onCreate,
    onUpdate,
    communityId,
    goal
}) => {
    const defaultCurrencyCode = `POCKET_${communityId}`;
    const isEdit = !!goal;

    const [name, setName] = useState('');
    const [timeZone, setTimeZone] = useState('Europe/Warsaw');
    const [tenantId, setTenantId] = useState('');
    const [dayFrom, setDayFrom] = useState('');
    const [dayTo, setDayTo] = useState('');
    const [goalRuleType, setGoalRuleType] = useState<GoalRuleType>(
        GoalRuleType.ACTIVITY_POINTS
    );
    const [goalRuleValue, setGoalRuleValue] = useState(10000);
    const [aggregationType, setAggregationType] = useState<GoalAggregationType>(
        GoalAggregationType.SUM
    );
    const [goalFitnessMetricType, setGoalFitnessMetricType] =
        useState<GoalFitnessMetricType>(GoalFitnessMetricType.STEPS);
    const [activityFilters, setActivityFilters] = useState<
        GoalActivityFilterFormValue[]
    >([]);
    const [recurrence, setRecurrence] = useState<RecurrenceType>(
        RecurrenceType.DAILY
    );
    const [streakEnabled, setStreakEnabled] = useState(true);
    const [assignAllUsers, setAssignAllUsers] = useState(true);
    const [userIds, setUserIds] = useState<string[]>([]);
    const [levels, setLevels] = useState<number[]>([]);
    const [rewards, setRewards] = useState<RewardRow[]>([]);
    const [visibleInApp, setVisibleInApp] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    React.useEffect(() => {
        if (!open) {
            return;
        }
        if (!goal) {
            setName('');
            setTimeZone('Europe/Warsaw');
            setTenantId('');
            setDayFrom('');
            setDayTo('');
            setGoalRuleType(GoalRuleType.ACTIVITY_POINTS);
            setGoalRuleValue(10000);
            setAggregationType(GoalAggregationType.SUM);
            setGoalFitnessMetricType(GoalFitnessMetricType.STEPS);
            setActivityFilters([]);
            setRecurrence(RecurrenceType.DAILY);
            setStreakEnabled(true);
            setAssignAllUsers(true);
            setUserIds([]);
            setLevels([]);
            setRewards([]);
            setVisibleInApp(true);
            return;
        }
        setName(goal.name || '');
        setDayFrom(goal.dayFrom || '');
        setDayTo(goal.dayTo || '');
        setGoalRuleType(goal.rule.type);
        setGoalRuleValue(goal.rule.value);
        setAggregationType(
            goal.rule.aggregationType ?? GoalAggregationType.SUM
        );
        setGoalFitnessMetricType(
            goal.rule.fitnessMetricType ?? GoalFitnessMetricType.STEPS
        );
        setActivityFilters(goalActivityFiltersFromSource(goal.rule));
        setRecurrence(goal.recurrence);
        setStreakEnabled(goal.streakEnabled ?? false);
        setLevels((goal.rule.levels || []).map((level) => level.threshold));
        setRewards(
            rewardRowsFromConfigs(goal.rewardConfigs, defaultCurrencyCode)
        );
        setVisibleInApp(goal.visibleInApp);
    }, [open, goal, defaultCurrencyCode]);

    const levelsValid = levels.every((threshold, index) =>
        index === 0 ? threshold > 0 : threshold > levels[index - 1]
    );

    const isRewardValid = (reward: RewardRow): boolean => {
        const targetValid =
            reward.kind === 'CURRENCY'
                ? reward.value > 0 && !!reward.currencyCode
                : reward.badge?.badgeLevelId != null;
        const conditionValid =
            reward.condition === RewardingType.LEVEL_INCREASED
                ? reward.minLevelValue >= 1
                : reward.condition === RewardingType.STREAK_INCREASED
                  ? reward.minStreakValue >= 1
                  : true;
        const limitValid = !reward.maxRewardsPeriod || reward.maxRewards >= 1;
        return targetValid && conditionValid && limitValid;
    };

    const rewardsValid = rewards.every(isRewardValid);

    const addLevel = () => {
        const last = levels.length ? levels[levels.length - 1] : 0;
        setLevels([...levels, last + 1]);
    };

    const updateLevel = (index: number, threshold: number) => {
        setLevels(levels.map((t, i) => (i === index ? threshold : t)));
    };

    const removeLevel = (index: number) => {
        setLevels(levels.filter((_, i) => i !== index));
    };

    const addReward = () => {
        setRewards([
            ...rewards,
            {
                condition: RewardingType.GOAL_REACHED,
                kind: 'CURRENCY',
                value: 1,
                currencyCode: defaultCurrencyCode,
                badge: null,
                minLevelValue: 1,
                minStreakValue: 2,
                maxRewards: 1,
                maxRewardsPeriod: ''
            }
        ]);
    };

    const updateReward = (index: number, patch: Partial<RewardRow>) => {
        setRewards(
            rewards.map((reward, i) =>
                i === index ? { ...reward, ...patch } : reward
            )
        );
    };

    const removeReward = (index: number) => {
        setRewards(rewards.filter((_, i) => i !== index));
    };

    const resolveRewardConfigs = (): RewardConfigDto[] =>
        rewards.map((reward) => ({
            rewardingType: reward.condition,
            ...(reward.kind === 'CURRENCY'
                ? { value: reward.value, currencyCode: reward.currencyCode }
                : { badgeLevelId: reward.badge?.badgeLevelId }),
            ...(reward.condition === RewardingType.LEVEL_INCREASED
                ? { minLevelValue: reward.minLevelValue }
                : {}),
            ...(reward.condition === RewardingType.STREAK_INCREASED
                ? { minStreakValue: reward.minStreakValue }
                : {}),
            ...(reward.maxRewardsPeriod
                ? {
                      limit: {
                          maxRewards: reward.maxRewards,
                          maxRewardsPeriod: reward.maxRewardsPeriod
                      }
                  }
                : {})
        }));

    const buildGoalRule = (): GoalRuleDto => {
        const activityFilterPayload =
            buildGoalActivityFilterPayload(activityFilters);
        const ruleLevels: GoalRuleLevelDto[] | undefined = levels.length
            ? levels.map((threshold, index) => ({
                  levelIndex: index + 1,
                  threshold
              }))
            : undefined;
        return {
            type: goalRuleType,
            value: ruleLevels ? ruleLevels[0].threshold : goalRuleValue,
            levels: ruleLevels,
            aggregationType:
                goalRuleType === GoalRuleType.ACTIVITY_RAW_VALUE
                    ? aggregationType
                    : undefined,
            fitnessMetricType:
                goalRuleType === GoalRuleType.ACTIVITY_RAW_VALUE
                    ? goalFitnessMetricType
                    : undefined,
            ...activityFilterPayload
        };
    };

    const ruleValueUnit = (): string => {
        if (goalRuleType === GoalRuleType.ACTIVITY_POINTS) {
            return 'activity points';
        }
        if (goalRuleType === GoalRuleType.ACTIVITY_RAW_VALUE) {
            return fitnessMetricLabels[goalFitnessMetricType].toLowerCase();
        }
        return 'activities';
    };

    const previewRule = buildGoalRule();
    const previewScope = goalActivityScope(previewRule);
    const previewRewards = resolveRewardConfigs()
        .map((rewardConfig, index) =>
            describeReward(rewardConfig, rewards[index].badge?.name)
        )
        .filter((reward): reward is string => !!reward);

    const handleSubmit = async () => {
        setSubmitting(true);
        const succeeded = goal
            ? await onUpdate(goal.key, {
                  communityId,
                  name,
                  rule: buildGoalRule(),
                  rewardConfigs: resolveRewardConfigs(),
                  recurrence,
                  streakEnabled,
                  visibleInApp,
                  dayFrom: dayFrom || undefined,
                  dayTo: dayTo || undefined
              })
            : await onCreate(
                  {
                      communityId,
                      name: name || undefined,
                      tenantId: tenantId || undefined,
                      timeZone,
                      dayFrom: dayFrom || undefined,
                      dayTo: dayTo || undefined,
                      rule: buildGoalRule(),
                      rewardConfigs: resolveRewardConfigs(),
                      streakEnabled,
                      userIdsToAssign: userIds,
                      autoAssignEnabled: true,
                      recurrence,
                      visibleInApp
                  },
                  assignAllUsers
              );
        setSubmitting(false);
        if (succeeded) {
            onClose();
        }
    };

    const dateHint = isEdit ? 'Cannot be cleared once set' : 'Optional';

    return (
        <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
            <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit
                            ? `Edit ${goalTitle(goal)}`
                            : 'Create community goal'}
                    </DialogTitle>
                    {isEdit && (
                        <DialogDescription>
                            {describeGoal(goal.rule, goal.recurrence)} ·{' '}
                            {goal.key}
                        </DialogDescription>
                    )}
                </DialogHeader>
                <div className="-mx-6 flex flex-col gap-4 overflow-y-auto px-6 py-1">
                    <div className="flex flex-col gap-1.5">
                        <TextField
                            label="Name (optional)"
                            type="text"
                            value={name}
                            onChange={(value) => setName(String(value))}
                        />
                        <Hint>
                            Only used to identify this goal in the admin list.
                            Leave empty to fall back to the summary below.
                        </Hint>
                    </div>

                    <div className="rounded-lg border p-4">
                        <p className="text-xs tracking-wide text-muted-foreground uppercase">
                            Members will see
                        </p>
                        <p className="text-lg font-semibold">
                            {describeGoal(previewRule, recurrence)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Counting{' '}
                            {previewScope || 'all supported activities'}
                            {levels.length > 0 &&
                                ` · ${levels.length} levels up to ${levels[levels.length - 1]}`}
                        </p>
                        <p
                            className={
                                previewRewards.length
                                    ? 'text-sm text-muted-foreground'
                                    : 'text-sm text-warning'
                            }
                        >
                            {previewRewards.length
                                ? `Rewards ${previewRewards.join(', ')}`
                                : 'No reward configured'}
                        </p>
                    </div>

                    {isEdit && (
                        <p className="text-sm text-warning">
                            Saving applies to the live goal immediately,
                            including today. Assignments and progress already
                            earned are kept.
                        </p>
                    )}

                    <Separator />
                    <SectionHeading
                        title="What members have to do"
                        hint="The rule type decides what the target number means."
                    />

                    <Select
                        label="Rule Type"
                        value={goalRuleType}
                        options={ruleTypeOptions}
                        onChange={(value: GoalRuleType) =>
                            setGoalRuleType(value)
                        }
                    />

                    {goalRuleType === GoalRuleType.ACTIVITY_RAW_VALUE && (
                        <div className="grid gap-4 md:grid-cols-2">
                            <Select
                                label="Fitness Metric"
                                value={goalFitnessMetricType}
                                options={fitnessMetricOptions}
                                onChange={(value: GoalFitnessMetricType) =>
                                    setGoalFitnessMetricType(value)
                                }
                            />
                            <Select
                                label="Aggregation Type"
                                value={aggregationType}
                                options={aggregationTypeOptions}
                                onChange={(value: GoalAggregationType) =>
                                    setAggregationType(value)
                                }
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-1.5">
                        <TextField
                            label="Target"
                            type="number"
                            value={levels.length ? levels[0] : goalRuleValue}
                            disabled={levels.length > 0}
                            onChange={(value) =>
                                setGoalRuleValue(parseInt(String(value)) || 0)
                            }
                        />
                        <Hint>
                            {levels.length
                                ? `Set by level 1 below, measured in ${ruleValueUnit()}`
                                : `Measured in ${ruleValueUnit()}`}
                        </Hint>
                    </div>

                    <GoalActivityFiltersEditor
                        value={activityFilters}
                        onChange={setActivityFilters}
                    />

                    <SectionHeading
                        title="Levels"
                        hint="Optional. Ascending thresholds that split the goal into stages."
                    />
                    {levels.map((threshold, index) => (
                        <div key={index} className="flex items-end gap-2">
                            <TextField
                                className="w-[200px]"
                                label={`Level ${index + 1} Threshold`}
                                type="number"
                                value={threshold}
                                onChange={(value) =>
                                    updateLevel(
                                        index,
                                        parseInt(String(value), 10) || 0
                                    )
                                }
                                error={
                                    index > 0 && threshold <= levels[index - 1]
                                        ? 'Must be greater than the previous level'
                                        : undefined
                                }
                            />
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeLevel(index)}
                                aria-label="Remove level"
                            >
                                <Trash2 />
                            </Button>
                        </div>
                    ))}
                    <Button
                        onClick={addLevel}
                        variant="outline"
                        className="self-start"
                    >
                        <Plus />
                        Add Level
                    </Button>
                    {levels.length > 0 && !levelsValid && (
                        <p className="text-xs text-destructive">
                            Thresholds must be positive and strictly increasing.
                        </p>
                    )}

                    <Separator />
                    <SectionHeading
                        title="Rewards"
                        hint="Each reward grants currency or a badge when its condition is met."
                    />
                    {hasSavedRewardLimit(goal) && (
                        <Alert variant="warning">
                            <AlertDescription>
                                Saving replaces the reward setup, which resets
                                every member&apos;s limit count for the current
                                period.
                            </AlertDescription>
                        </Alert>
                    )}
                    {rewards.map((reward, index) => (
                        <div
                            key={index}
                            className="flex flex-col gap-4 rounded-lg border p-4"
                        >
                            <div className="flex items-end gap-2">
                                <Select
                                    label="Condition"
                                    value={reward.condition}
                                    options={rewardConditions}
                                    onChange={(value: RewardingType) =>
                                        updateReward(index, {
                                            condition: value
                                        })
                                    }
                                />
                                <Select
                                    label="Reward"
                                    value={reward.kind}
                                    options={rewardKindOptions}
                                    onChange={(value: RewardKind) =>
                                        updateReward(index, { kind: value })
                                    }
                                />
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0"
                                    onClick={() => removeReward(index)}
                                    aria-label="Remove reward"
                                >
                                    <Trash2 />
                                </Button>
                            </div>

                            {reward.condition ===
                                RewardingType.LEVEL_INCREASED && (
                                <TextField
                                    className="w-[200px]"
                                    label="Min Level"
                                    type="number"
                                    value={reward.minLevelValue}
                                    onChange={(value) =>
                                        updateReward(index, {
                                            minLevelValue:
                                                parseInt(String(value), 10) || 0
                                        })
                                    }
                                />
                            )}
                            {reward.condition ===
                                RewardingType.STREAK_INCREASED && (
                                <TextField
                                    className="w-[200px]"
                                    label="Min Streak"
                                    type="number"
                                    value={reward.minStreakValue}
                                    onChange={(value) =>
                                        updateReward(index, {
                                            minStreakValue:
                                                parseInt(String(value), 10) || 0
                                        })
                                    }
                                />
                            )}

                            {reward.kind === 'CURRENCY' ? (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <TextField
                                        label="Value"
                                        type="number"
                                        value={reward.value}
                                        onChange={(value) =>
                                            updateReward(index, {
                                                value:
                                                    parseInt(
                                                        String(value),
                                                        10
                                                    ) || 0
                                            })
                                        }
                                    />
                                    <CurrencySelect
                                        value={reward.currencyCode}
                                        onChange={(currencyCode) =>
                                            updateReward(index, {
                                                currencyCode
                                            })
                                        }
                                    />
                                </div>
                            ) : (
                                <BadgePicker
                                    value={reward.badge}
                                    onChange={(badge) =>
                                        updateReward(index, { badge })
                                    }
                                    label="Badge"
                                />
                            )}

                            <div className="flex items-end gap-4">
                                <div className="w-[200px]">
                                    <Select
                                        label="Limit"
                                        value={reward.maxRewardsPeriod}
                                        options={rewardLimitOptions}
                                        onChange={(
                                            value: RecurrenceType | ''
                                        ) =>
                                            updateReward(index, {
                                                maxRewardsPeriod: value
                                            })
                                        }
                                    />
                                </div>
                                {reward.maxRewardsPeriod && (
                                    <TextField
                                        className="w-[220px]"
                                        label="Max payouts per member"
                                        type="number"
                                        value={reward.maxRewards}
                                        onChange={(value) =>
                                            updateReward(index, {
                                                maxRewards:
                                                    parseInt(
                                                        String(value),
                                                        10
                                                    ) || 0
                                            })
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                    <Button
                        onClick={addReward}
                        variant="outline"
                        className="self-start"
                    >
                        <Plus />
                        Add Reward
                    </Button>

                    <Separator />
                    <SectionHeading
                        title="When it runs"
                        hint="Recurrence decides how often progress resets."
                    />

                    <Select
                        label="Recurrence"
                        value={recurrence}
                        options={recurrenceOptions}
                        onChange={(value: RecurrenceType) =>
                            setRecurrence(value)
                        }
                    />

                    <div className="grid gap-4 md:grid-cols-2">
                        <DateField
                            label="Start Date"
                            value={dayFrom}
                            onChange={setDayFrom}
                            hint={dateHint}
                        />
                        <DateField
                            label="End Date"
                            value={dayTo}
                            onChange={setDayTo}
                            hint={dateHint}
                        />
                    </div>

                    {!isEdit && (
                        <Combobox
                            label="Time Zone"
                            value={timeZone || null}
                            onChange={(value) => setTimeZone(value || '')}
                            options={timeZoneOptions()}
                            getOptionLabel={(option) => option}
                            helperText="Decides when a day starts and ends for this goal"
                        />
                    )}

                    <Separator />
                    <SectionHeading title="Visibility" />

                    <SwitchField
                        label="Visible in mobile app"
                        checked={visibleInApp}
                        onChange={setVisibleInApp}
                    />
                    <p className="text-sm text-muted-foreground">
                        When off, the goal is hidden from the goal list and the
                        dashboard. Progress and rewards keep running.
                    </p>

                    <Checkbox
                        center
                        checked={streakEnabled}
                        onChange={(_, checked) => setStreakEnabled(checked)}
                    >
                        Show streak counter
                    </Checkbox>

                    {!isEdit && (
                        <>
                            <Separator />
                            <SectionHeading title="Who gets it" />

                            <SwitchField
                                label="Assign all community members"
                                checked={assignAllUsers}
                                onChange={setAssignAllUsers}
                            />

                            {!assignAllUsers && (
                                <>
                                    <UserIdsInput
                                        value={userIds}
                                        onChange={setUserIds}
                                    />
                                    <p className="text-sm text-muted-foreground">
                                        {userIds.length} member
                                        {userIds.length === 1 ? '' : 's'}{' '}
                                        selected
                                    </p>
                                </>
                            )}

                            <Accordion type="single" collapsible>
                                <AccordionItem value="advanced">
                                    <AccordionTrigger>
                                        Advanced
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <div className="flex flex-col gap-1.5">
                                            <TextField
                                                label="Tenant ID"
                                                type="text"
                                                value={tenantId}
                                                onChange={(value) =>
                                                    setTenantId(String(value))
                                                }
                                            />
                                            <Hint>
                                                Leave empty unless this goal
                                                belongs to a single tenant
                                            </Hint>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            submitting ||
                            (!isEdit && !timeZone) ||
                            (!levels.length && goalRuleValue <= 0) ||
                            !levelsValid ||
                            !rewardsValid
                        }
                    >
                        {!isEdit && <Plus />}
                        {isEdit ? 'Save Changes' : 'Create Goal'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
